import { inject } from '@angular/core';

import { injectMutation } from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import { CompanyApiService } from '../services/company-api.service';

/**
 * Create (PUT /company/create) or update (POST /company/update) a company. The
 * payload is the already-snake_cased, nested company-settings object; the caller
 * builds it and says which of the two it is - the backend guards them differently.
 *
 * No query invalidation - the companies list is a server-side ag-grid rather than
 * a query, and the editor navigates away on success.
 */
export function injectManageCompanyMutation() {
  const companyApi = inject(CompanyApiService);

  return injectMutation(() => ({
    mutationFn: ({
      settingsInSnakeCase,
      isCreate,
    }: {
      settingsInSnakeCase: any;
      isCreate: boolean;
    }) =>
      firstValueFrom(
        isCreate
          ? companyApi.createCompany(settingsInSnakeCase)
          : companyApi.updateCompany(settingsInSnakeCase),
      ),
  }));
}

/**
 * Enable/disable a user (POST /company/user_toggle). The endpoint takes only the
 * user id and flips the flag server-side.
 */
export function injectToggleUserStatusMutation() {
  const companyApi = inject(CompanyApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (userId: string) =>
      firstValueFrom(companyApi.toggleUserStatus(userId)),
    onError: () =>
      toast.showError(translate.instant('admin-module.msg.user-update-failed')),
  }));
}
